import { z } from 'zod';

import { CompanyId, PoolId, PoolInternalId } from './BrandedIds';
import { RemittanceType } from './RemittanceType';
import { zodDateOrString } from './utils/Zod';

export const PoolStatus = z.enum(['PENDING_ACTIVATION', 'ACTIVE']);
export type PoolStatus = z.infer<typeof PoolStatus>;

export const Pool = z.object({
  id: PoolInternalId,
  poolId: PoolId,
  companyId: CompanyId,
  status: PoolStatus,
  investor: z.string().optional(),
  remittanceType: RemittanceType.optional(),

  clearingAccountLastFour: z.string().optional(),
  principalAndInterestAccountLastFour: z.string().optional(),
  taxAndInsuranceAccountLastFour: z.string().optional(),
  servicingFeesAccountLastFour: z.string().optional(),
  suspenseAccountLastFour: z.string().optional(),
  guaranteeFeesAccountLastFour: z.string().optional(),
  guaranteeFeesAccountNotApplicable: z.boolean(),

  createdAt: zodDateOrString,
  updatedAt: zodDateOrString,
});
export type Pool = z.infer<typeof Pool>;

export const PoolInputComplete = z
  .object({
    poolId: PoolId,
    investor: z.string().min(1),
    remittanceType: RemittanceType,
    clearingAccountLastFour: z.string().length(4),
    principalAndInterestAccountLastFour: z.string().length(4),
    taxAndInsuranceAccountLastFour: z.string().length(4),
    servicingFeesAccountLastFour: z.string().length(4),
    suspenseAccountLastFour: z.string().length(4),
    guaranteeFeesAccountLastFour: z.string().length(4).or(z.literal('')).nullable().optional(),
    guaranteeFeesAccountNotApplicable: z.boolean(),
  })
  .superRefine((data, ctx) => {
    if (
      data.guaranteeFeesAccountNotApplicable !== true &&
      (!data.guaranteeFeesAccountLastFour || data.guaranteeFeesAccountLastFour.length !== 4)
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Required',
        path: ['guaranteeFeesAccountLastFour'],
      });
    }
  });

export const PoolInputNullable = z.object({
  poolId: PoolId,
  status: PoolStatus,
  investor: z.string().nullable().optional(),
  remittanceType: RemittanceType.nullable().optional(),
  clearingAccountLastFour: z.string().length(4).or(z.literal('')).nullable().optional(),
  principalAndInterestAccountLastFour: z.string().length(4).or(z.literal('')).nullable().optional(),
  taxAndInsuranceAccountLastFour: z.string().length(4).or(z.literal('')).nullable().optional(),
  servicingFeesAccountLastFour: z.string().length(4).or(z.literal('')).nullable().optional(),
  suspenseAccountLastFour: z.string().length(4).or(z.literal('')).nullable().optional(),
  guaranteeFeesAccountLastFour: z.string().length(4).or(z.literal('')).nullable().optional(),
  guaranteeFeesAccountNotApplicable: z.boolean(),
});
export type PoolInputNullable = z.infer<typeof PoolInputNullable>;
